'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Menu, X } from 'lucide-react'
import { Logo } from '@/components/logo'
import { Icon } from '@/components/icon'
import { CtaButton } from '@/components/cta-button'
import { MerchantUserSwitch } from '@/components/merchant-user-switch'
import { solutionsMenu, productsMenu, resourcesMenu, EXTERNAL, type MenuItem } from '@/lib/site'
import { cn } from '@/lib/utils'

const MENUS: { label: string; items: MenuItem[] }[] = [
  { label: 'Products', items: productsMenu },
  { label: 'Solutions', items: solutionsMenu },
  { label: 'Resources', items: resourcesMenu },
]

const SIMPLE_LINKS = [
  { label: 'API Docs', href: 'https://merchant.rombex.com/api-docs', external: true },
  { label: 'Company', href: '/company' },
]

export function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-300',
        scrolled
          ? 'border-b border-border/70 bg-background/80 backdrop-blur-xl'
          : 'border-b border-transparent bg-transparent',
      )}
      onMouseLeave={() => setOpen(null)}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Logo />

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 lg:flex">
          {/* Products and Solutions dropdowns */}
          {MENUS.slice(0, 2).map((menu) => (
            <div key={menu.label} onMouseEnter={() => setOpen(menu.label)} className="static">
              <button
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-colors',
                  open === menu.label ? 'text-brand' : 'text-foreground/80 hover:text-foreground',
                )}
                aria-expanded={open === menu.label}
              >
                {menu.label}
                <ChevronDown
                  className={cn('size-4 transition-transform', open === menu.label && 'rotate-180')}
                />
              </button>
            </div>
          ))}
          
          {/* Simple links */}
          {SIMPLE_LINKS.map((l) => (
            l.external ? (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
                onMouseEnter={() => setOpen(null)}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                href={l.href}
                className="rounded-full px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
                onMouseEnter={() => setOpen(null)}
              >
                {l.label}
              </Link>
            )
          ))}
          
          {/* Resources dropdown */}
          <div onMouseEnter={() => setOpen('Resources')} className="static">
            <button
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-colors',
                open === 'Resources' ? 'text-brand' : 'text-foreground/80 hover:text-foreground',
              )}
              aria-expanded={open === 'Resources'}
            >
              Resources
              <ChevronDown
                className={cn('size-4 transition-transform', open === 'Resources' && 'rotate-180')}
              />
            </button>
          </div>
        </div>

        {/* Right actions */}
        <div className="hidden items-center gap-3 lg:flex">
          <MerchantUserSwitch />
          <CtaButton href={EXTERNAL.requestDemo} arrow>
            Request Demo
          </CtaButton>
        </div>

        {/* Mobile toggle */}
        <button
          className="inline-flex size-10 items-center justify-center rounded-lg text-foreground lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </nav>

      {/* Mega menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            key={open}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute inset-x-0 top-16 hidden lg:block"
            onMouseEnter={() => setOpen(open)}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="overflow-hidden rounded-2xl border border-border bg-popover/95 p-3 shadow-xl backdrop-blur-xl">
                <div className="grid grid-cols-2 gap-1 xl:grid-cols-3">
                  {MENUS.find((m) => m.label === open)?.items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setOpen(null)}
                      className="group flex gap-3 rounded-xl p-3 transition-colors hover:bg-accent"
                    >
                      <span className="flex size-10 shrink-0 items-center justify-center text-muted-foreground transition-colors group-hover:text-foreground">
                        <Icon name={item.icon} className="size-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground">
                          {item.title}
                        </span>
                        <span className="block text-sm leading-snug text-muted-foreground">
                          {item.description}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-border bg-background lg:hidden"
          >
            <div className="max-h-[75vh] space-y-6 overflow-y-auto px-4 py-6">
              {MENUS.map((menu) => (
                <div key={menu.label}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {menu.label}
                  </p>
                  <div className="grid gap-1">
                    {menu.items.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 rounded-lg p-2 hover:bg-accent"
                      >
                        <Icon name={item.icon} className="size-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              <div className="grid gap-1">
                {SIMPLE_LINKS.map((l) => (
                  l.external ? (
                    <a
                      key={l.label}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileOpen(false)}
                      className="rounded-lg p-2 text-sm font-medium hover:bg-accent"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      key={l.label}
                      href={l.href}
                      onClick={() => setMobileOpen(false)}
                      className="rounded-lg p-2 text-sm font-medium hover:bg-accent"
                    >
                      {l.label}
                    </Link>
                  )
                ))}
              </div>
              <div className="flex flex-col gap-3 pt-2">
                <MerchantUserSwitch className="self-start" />
                <CtaButton href={EXTERNAL.requestDemo} arrow>
                  Request Demo
                </CtaButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
